/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import type { DiscountableItem } from "./types";
import { useAuth } from "@app/vue/providers/Auth";

interface DiscountItemRowProps {
  item: DiscountableItem;
  selectedQuantity: number;
  existingDiscountAmount: number;
  onSelect: (key: string, qty: number) => void;
}

export const DiscountItemRow = defineComponent<DiscountItemRowProps>({
  name: "DiscountItemRow",
  props: ["item", "selectedQuantity", "existingDiscountAmount", "onSelect"],
  setup(props) {
    const auth = useAuth();
    const isSelected = computed(() => props.selectedQuantity > 0);

    const unitPrice = computed(() => props.item.total / props.item.quantity);

    const displayedTotal = computed(() => {
      const qty = isSelected.value
        ? props.selectedQuantity
        : props.item.quantity;
      return unitPrice.value * qty;
    });

    const discountedTotal = computed(() => {
      if (props.existingDiscountAmount <= 0) return null;
      // Prorate existing discount by currently displayed quantity
      const qty = isSelected.value
        ? props.selectedQuantity
        : props.item.quantity;
      const ratio = qty / props.item.quantity;
      return displayedTotal.value - props.existingDiscountAmount * ratio;
    });

    const toggle = () => {
      props.onSelect(
        props.item.key,
        isSelected.value ? 0 : props.item.quantity,
      );
    };

    const decrement = () =>
      props.onSelect(props.item.key, Math.max(0, props.selectedQuantity - 1));

    const increment = () =>
      props.onSelect(
        props.item.key,
        Math.min(props.item.quantity, props.selectedQuantity + 1),
      );

    return () => (
      <div class="flex justify-between items-center p-3 border  rounded">
        <div class="flex items-center gap-2 flex-1">
          <label class="cursor-pointer label p-0 flex justify-center items-center">
            <input
              type="checkbox"
              class="checkbox checkbox-sm"
              checked={isSelected.value}
              onChange={toggle}
            />
          </label>
          <div class="flex flex-col flex-1 min-w-0">
            <div
              class={`bg-transparent border-none pr-2 flex-1 min-h-8 flex justify-start items-center cursor-pointer ${
                isSelected.value ? "text-accent" : "text-accent/80"
              }`}
              onClick={toggle}
            >
              {props.item.label}
            </div>
            <div class="text-xs text-base-content/50 pr-2 hidden">
              {props.item.detail}
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 text-accent">
          {/* Price – hidden when selected (qty controls shown instead), except when discounted */}
          {!isSelected.value ? (
            discountedTotal.value !== null ? (
              <div class="text-right mr-1">
                <div class="text-xs line-through text-accent/40">
                  {formatPrice(
                    displayedTotal.value,
                    true,
                    2,
                    auth.user.value?.restaurant.currency || "CHF",
                  )}
                </div>
                <div class="text-sm font-semibold text-success">
                  {formatPrice(
                    discountedTotal.value,
                    true,
                    2,
                    auth.user.value?.restaurant.currency || "CHF",
                  )}
                </div>
              </div>
            ) : (
              <span class="text-accent/50">
                {formatPrice(
                  displayedTotal.value,
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </span>
            )
          ) : null}

          {/* Decrement */}
          <button
            onClick={decrement}
            class={
              "btn btn-xs btn-circle btn-neutral " +
              (isSelected.value ? "" : "hidden")
            }
          >
            <span class="material-symbols-outlined text-sm">remove</span>
          </button>

          {/* Quantity badge */}
          <span class="w-16 bg-neutral px-2 rounded text-center font-mono">
            {isSelected.value
              ? `${props.selectedQuantity}/${props.item.quantity}`
              : props.item.quantity}
          </span>

          {/* Increment */}
          <button
            onClick={increment}
            class={
              "btn btn-xs btn-circle btn-neutral " +
              (isSelected.value ? "" : "hidden")
            }
            disabled={props.selectedQuantity >= props.item.quantity}
          >
            <span class="material-symbols-outlined text-sm">add</span>
          </button>
        </div>
      </div>
    );
  },
});
